import { ChevronsUpDown, type LucideIcon } from "lucide-react";
import { useState } from "react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ScopeOption {
  id: string;
  label: string;
  /** Optional second line, e.g. a footprint or a code. */
  hint?: string;
}

/**
 * The sidebar scope picker: a searchable dropdown under the module switcher
 * that names and changes what the module below it is scoped to.
 *
 * This is the chrome only. It was extracted from `AdvisorClientPicker` when
 * Market Intelligence became the second record-scoped module — that file's own
 * comment asked for exactly this rather than a branch. Everything specific to
 * what is being picked (the axis name, the icon, the options, what selecting
 * one does) arrives as props.
 *
 * The visual decisions are inherited and deliberate, so they are recorded here
 * rather than lost in the move:
 *
 * **Distinguished from the module switcher by ACCENT, not weight.** Two
 * earlier attempts bracketed it. Cloning the switcher's bordered elevated card
 * made two identical pills read as peers, when the scope sits *inside* the
 * module. Stripping it to a bare uppercase caption fixed that but went too far
 * — it stopped reading as a control at all, which is wrong for the thing
 * driving the whole area. So: a filled tinted surface with a left accent
 * stripe. The same footprint as a nav row, since it heads the rows below it,
 * but unmistakably a control, and tinted so it never reads as one more row.
 *
 * **The eyebrow is always on.** Naming the axis is what makes the control
 * self-explanatory: a lone name is ambiguous between "you are here" and "you
 * can change this".
 */
export function ScopePicker({
  axis,
  icon: Icon,
  selected,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  options,
  isLoading = false,
  onSearch,
  onSelect,
}: {
  /** The axis being scoped, e.g. "Client" or "Region". Shown as the eyebrow. */
  axis: string;
  icon: LucideIcon;
  /** What is in scope, or `null` when nothing is chosen yet. */
  selected: { id: string; label: string } | null;
  /** Trigger label when nothing is in scope. */
  placeholder: string;
  searchPlaceholder: string;
  /** Shown when the list is empty and not loading. */
  emptyMessage: string;
  options: ScopeOption[];
  isLoading?: boolean;
  /**
   * Called as the user types. Omit for a bounded list — `Command` then filters
   * client-side, which is right when every option is already present.
   * Provide it for an unbounded one, where filtering a fetched page would
   * silently hide matches that are not on it.
   */
  onSearch?: (query: string) => void;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const choose = (id: string) => {
    setOpen(false);
    setQuery("");
    onSearch?.("");
    onSelect(id);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            selected
              ? `Switch ${axis.toLowerCase()} — currently ${selected.label}`
              : placeholder
          }
          className={cn(
            "group flex w-full min-w-0 items-center gap-2 rounded-md border-l-2 py-1.5 pl-2 pr-1.5 text-left transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            selected
              ? "border-l-primary bg-primary-dim hover:bg-row-hover data-[state=open]:bg-row-hover"
              : // Nothing chosen: dashed stripe + neutral fill, so the empty
                // state reads as "a choice is pending here" rather than as a
                // filled control whose label happens to be grey.
                "border-l-border-strong border-dashed bg-surface-elevated hover:bg-row-hover data-[state=open]:bg-row-hover",
          )}
        >
          <Icon
            size={14}
            strokeWidth={1.75}
            className={cn("shrink-0", selected ? "text-primary" : "text-muted-foreground")}
          />
          <span className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-xs font-bold uppercase tracking-eyebrow text-text-dim">
              {axis}
            </span>
            <span
              className={cn(
                "truncate text-sm",
                selected ? "font-semibold text-foreground" : "text-muted-foreground",
              )}
            >
              {selected ? selected.label : placeholder}
            </span>
          </span>
          <ChevronsUpDown
            size={14}
            strokeWidth={1.75}
            className={cn(
              "shrink-0 transition-colors",
              selected
                ? "text-primary/70 group-hover:text-primary"
                : "text-text-dim group-hover:text-foreground",
            )}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" sideOffset={8} className="w-64 p-0">
        <Command shouldFilter={!onSearch} className="border-0">
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={(next) => {
              setQuery(next);
              onSearch?.(next);
            }}
            autoFocus
          />
          <CommandList>
            <CommandEmpty>{isLoading ? "Searching…" : emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  // With client-side filtering the value IS what gets matched,
                  // so it must be the label rather than an opaque id.
                  value={onSearch ? option.id : `${option.label} ${option.hint ?? ""}`}
                  onSelect={() => choose(option.id)}
                  className={cn(
                    "gap-2",
                    option.id === selected?.id && "font-semibold text-primary",
                  )}
                >
                  <span className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate">{option.label}</span>
                    {option.hint ? (
                      <span className="truncate text-xs text-muted-foreground">
                        {option.hint}
                      </span>
                    ) : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
